import { useState } from "react";
import {
  LAB_RESULT_OPS_QUEUE_LABEL,
  LAB_RESULT_WAITING_TITLE,
  type WorkQueueItem,
  type WorkQueueLane,
} from "@care-by-ella/contracts";
import {
  activeItems,
  isEscalatedLabCheck,
  isWaitingLabCheck,
} from "@/lib/lab-result-check";
import { laneItems, laneNote, useClinicQueues } from "@/lib/clinic-queues";
import { LANE_LABEL, WorkQueueRow } from "./WorkQueue";

// TES-506 — one category, one page.
//
// The 2026-09-04 walkthrough: no inbox at all, run each category as a menu item
// with a badge. The aggregate /work-queue rendered all six doctor lanes stacked
// on one scroll, which is the third of the three inboxes the doctor was asked
// to triage. The lanes were never the problem — the server already decides them
// per role, prioritized — so this route renders exactly one of them and the
// sidebar carries the counts. The coach keeps the stacked view: her whole
// workspace is that queue, and she has four lanes, not the doctor's six.
//
// Reads the shell's shared fetch (clinic-queues) rather than fetching its own,
// so the number on the badge and the number on the page are the same number.

const LANE_INTRO: Partial<Record<WorkQueueLane, string>> = {
  renewals:
    "Månadsincheckningar som väntar på beslut. Läs patientens svar, skriv återkopplingen och godkänn förnyelsen i ett steg.",
  side_effects:
    "Biverkningar som patienter rapporterat i sin månadsincheckning, för cykler som väntar på ditt beslut.",
  medical_reviews:
    "Medicinska svar i veckoincheckningen och meddelanden som coachen skickat till läkare. Brådskande överst. Ett ärende är hanterat först när du öppnat originalsvaren och markerat det som granskat.",
  medical_alerts:
    "Oåtgärdade medicinska riskflaggor. Högst prioritet i portalen.",
  handoffs: "Ärenden som lämnats över till dig.",
  follow_ups:
    "Påminnelser du själv satt med Följ upp senare, nu när datumet har kommit.",
  checkins:
    "Veckoincheckningar med flaggor som ingen tittat på än. Öppna patienten, läs, och markera som granskad.",
  // TES-1284 — proposed copy.
  labs: "Provsvar att granska och skicka, provsvar att kontrollera i Webdoc och beställningar som väntar på svar.",
  doctor_question_answers:
    "Patienter som har svarat på en fråga du ställt med Besked med fråga. Välj Hantera svar för att läsa beskedet, frågan och hela svaret här, och markera det som hanterat eller skicka ett nytt besked.",
  completion_rounds:
    "Patienter som har gjort allt du begärt i en komplettering. Ett samlat ärende per komplettering – delvis inskickade kompletteringar och provsvar som ingår väntar här tills allt är klart.",
};

export function LaneQueueRoute({ lane }: { lane: WorkQueueLane }) {
  const { workQueue, loading, reload } = useClinicQueues();
  // TES-757 — see WorkQueueRoute: the server's "next" opens itself.
  const [nextItemId, setNextItemId] = useState<string | null>(null);

  // TES-1284 — a lab check waiting for its next time has left the active
  // list; it is listed under it with the time it comes back.
  const all = laneItems(workQueue, lane);
  const items = activeItems(all);
  const waiting = all.filter(isWaitingLabCheck);
  // TES-1401 (Ö-473) — and an escalated one sits with the operational queue.
  const withOps = all.filter(isEscalatedLabCheck);
  const note = laneNote(workQueue, lane);
  const intro = LANE_INTRO[lane];
  const row = (item: WorkQueueItem) => (
    <li key={item.id}>
      <WorkQueueRow
        item={item}
        autoExpand={item.id === nextItemId}
        onDisposed={(next) => {
          setNextItemId(next?.id ?? null);
          reload();
        }}
      />
    </li>
  );

  return (
    <div className="flex flex-col gap-md">
      <div>
        <h1 className="font-display text-3xl tracking-tight">
          {LANE_LABEL[lane]}
        </h1>
        {intro ? (
          <p className="mt-xs max-w-2xl text-sm text-textSecondary">{intro}</p>
        ) : null}
      </div>

      {loading && !workQueue ? (
        <p className="text-sm text-textSecondary">Hämtar…</p>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-surfaceMuted bg-surface p-md text-sm text-textSecondary">
          {note ?? "Inget att göra just nu."}
        </div>
      ) : (
        <ul className="flex flex-col gap-sm">{items.map(row)}</ul>
      )}

      {waiting.length > 0 ? (
        <section className="flex flex-col gap-sm">
          <h2 className="font-display text-lg tracking-tight">
            {LAB_RESULT_WAITING_TITLE}
          </h2>
          <ul className="flex flex-col gap-sm">{waiting.map(row)}</ul>
        </section>
      ) : null}

      {withOps.length > 0 ? (
        <section className="flex flex-col gap-sm">
          <h2 className="font-display text-lg tracking-tight">
            I kön {LAB_RESULT_OPS_QUEUE_LABEL}
          </h2>
          <p className="max-w-2xl text-sm text-textSecondary">
            Driften kontrollerar remiss, genomförd provtagning och överföring
            från LabPortalen/Webdoc. Du kan fortfarande registrera provsvaret
            här när det finns.
          </p>
          <ul className="flex flex-col gap-sm">{withOps.map(row)}</ul>
        </section>
      ) : null}
    </div>
  );
}
